import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrivalPrepCard, FlowLayout, OptionGrid, RecommendationCard, Skeleton, StatusChip } from '@/ds'
import { formatMoney, locale, time, weekdayLong } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { useAsync } from '@/lib/useAsync'
import { featureEnabled } from '@/config/features'
import { checkinService } from '@/services/checkin'
import type { ArrivalSlot, Transport } from '@/services/checkin'
import { climaService } from '@/services/clima'
import { SectionError } from '../../Estancia/sections/SectionError'
import { BOOKING_ROUTES, changeQuery } from '../../Reserva/shared'
import { useCheckinFlow } from '../CheckinFlow'
import { backLabelKey, neighbours, progressFor, stepPath } from '../flow'
import { useSave } from '../useSave'

const TRANSPORTS: Transport[] = ['plane_metro', 'train', 'car', 'taxi']
const slotId = (s: ArrivalSlot) => s.from
const UNKNOWN = 'unknown'

/** "Sábado 3 de octubre" */
function longDate(iso: string, lang: string): string {
  const s = new Intl.DateTimeFormat(locale(lang), { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso)).replace(',', '')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * Paso 6 · ¿A qué hora llegáis? (CheckIn.dc.html step=6, Bloque 8 §8i). Sin endpoint en la API actual:
 * hora de llegada, transporte, entrada anticipada y preparar la temperatura van contra mock (D29).
 * La temperatura es opcional y no bloquea el paso: si no se toca, no se programa nada.
 */
export function Llegada() {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  const { go } = useAppNavigate()
  const { stay, draft } = useCheckinFlow()
  const inRegistration = draft.mode === 'register' && draft.persona === 'holder' && draft.documents.length > 0
  const { prev, next } = neighbours('6', stay, 'holder', draft.method, draft.entry)
  const [arrival, reloadArrival] = useAsync(`arr:${stay.guestCode}`, (signal) => checkinService.getArrival(stay, signal))
  const [options, reloadOptions] = useAsync(`arropt:${stay.guestCode}`, (signal) => checkinService.getArrivalOptions(stay, signal))
  const showArrivalPrep = featureEnabled('climaPrepararLlegada')
  const [prep] = useAsync(`prep:${stay.guestCode}`, (signal) => (showArrivalPrep ? climaService.getArrivalPrep(stay, signal) : Promise.resolve(null)))
  const [slot, setSlot] = useState<string | null>(null)
  const [transport, setTransport] = useState<Transport | null>(null)
  const [target, setTarget] = useState<number | null>(null)
  const [missingSlot, setMissingSlot] = useState(false)
  const { saving, error, run } = useSave()

  const current = arrival.kind === 'ready' ? arrival.value : null
  const currentSlot = current?.etaSlot ? ('unknown' in current.etaSlot ? UNKNOWN : current.etaSlot.from) : null
  const chosenSlot = slot ?? currentSlot
  const chosenTransport = transport ?? (current?.transport && TRANSPORTS.includes(current.transport) ? current.transport : null)
  const prepValue = showArrivalPrep && prep.kind === 'ready' ? prep.value : null
  const chosenTarget = target ?? prepValue?.target ?? null
  const slots = options.kind === 'ready' ? options.value.slots : []
  const selected = slots.find((s) => slotId(s) === chosenSlot) ?? null
  const earlyOffer = options.kind === 'ready' ? options.value.earlyCheckin : null
  const earlyAdded = earlyOffer?.added ?? false

  const done = () => (inRegistration ? go(stepPath(next)) : draft.mode === 'arrival' ? go(stepPath('8')) : go('/'))

  const save = async () => {
    if (!chosenSlot) return setMissingSlot(true)
    const etaSlot = chosenSlot === UNKNOWN || !selected ? ({ unknown: true } as const) : { from: selected.from, to: selected.to }
    const ok = await run(async () => {
      await checkinService.setArrival(stay, { etaSlot, transport: chosenTransport ?? 'unknown' })
      if (target !== null && prepValue) await climaService.setArrivalPrep(stay, target)
      return true
    })
    if (ok) done()
  }

  const loading = arrival.kind === 'loading' || options.kind === 'loading'
  const failed = arrival.kind === 'error' ? arrival : options.kind === 'error' ? options : null

  return (
    <FlowLayout
      progress={{ ...progressFor('6', stay, 'holder', draft.method, draft.entry), backLabel: t(inRegistration ? backLabelKey(prev) : 'v2.checkin.atras.estancia'), onBack: () => (inRegistration ? go(stepPath(prev)) : go('/')) }}
      title={t('v2.checkin.llegada.titulo')}
      intro={t('v2.checkin.llegada.intro', { day: longDate(stay.dates.checkIn, lang), time: time(stay.dates.checkIn, lang) })}
      primary={loading || failed ? undefined : { label: t('v2.checkin.llegada.guardar'), onClick: () => void save(), loading: saving }}
      footerMessage={error}
    >
      {loading && (
        <div aria-busy="true" className="checkin__stack">
          <Skeleton height={140} radius={12} />
          <Skeleton height={48} radius={999} />
        </div>
      )}
      {failed && failed.kind === 'error' && (
        <SectionError
          code={failed.code}
          onRetry={() => {
            reloadArrival()
            reloadOptions()
          }}
        />
      )}
      {!loading && !failed && (
        <>
          <OptionGrid
            label={t('v2.checkin.llegada.hora')}
            className="checkin__slots"
            columns={3}
            tabular
            value={chosenSlot}
            onChange={(id) => {
              setSlot(id)
              setMissingSlot(false)
            }}
            options={[...slots.map((s) => ({ id: slotId(s), label: s.to ? t('v2.checkin.llegada.franja', { from: s.from, to: s.to }) : t('v2.checkin.llegada.despues', { from: s.from }) })), { id: UNKNOWN, label: t('v2.checkin.llegada.noLoSe') }]}
            help={missingSlot ? <span className="checkin__error" role="alert">{t('v2.checkin.llegada.eligeFranja')}</span> : undefined}
          />
          <OptionGrid
            label={
              <>
                {t('v2.checkin.llegada.comoVenis')} <span className="checkin__label-note">{t('v2.checkin.llegada.comoVenisNota')}</span>
              </>
            }
            size="chip"
            value={chosenTransport}
            onChange={(id) => setTransport(id as Transport)}
            options={TRANSPORTS.map((id) => ({ id, label: t(`v2.checkin.llegada.transporte.${id}`) }))}
          />
          {prepValue && chosenTarget !== null && (
            <ArrivalPrepCard
              target={chosenTarget}
              min={prepValue.min}
              max={prepValue.max}
              onTargetChange={setTarget}
              text={t('v2.checkin.llegada.climaTexto')}
              confirmation={
                target !== null || prepValue.saved
                  ? { text: selected?.to ? t('v2.checkin.llegada.climaConfirmacion', { from: selected.from, to: selected.to }) : t('v2.checkin.llegada.climaConfirmacionSinHora') }
                  : null
              }
            />
          )}
          {earlyOffer?.available && (
            <RecommendationCard
              reason={t('v2.checkin.llegada.anticipadaMotivo')}
              title={t('v2.checkin.llegada.anticipadaTitulo', { time: earlyOffer.from })}
              price={formatMoney(earlyOffer.price, lang)}
              {...(earlyAdded
                ? {}
                : {
                    actionLabel: t('v2.reserva.cambios.early_checkin'),
                    // Un solo camino para entrar antes: el cambio de reserva (D47), que vuelve a este paso.
                    onAction: () => go(BOOKING_ROUTES.pick('early_checkin'), { query: changeQuery('checkin') }),
                  })}
            >
              {t('v2.checkin.llegada.anticipadaTexto', { weekday: weekdayLong(stay.dates.checkIn, lang) })}
              {earlyAdded && (
                <span className="checkin__block">
                  <StatusChip tone="success">{t('v2.checkin.llegada.anticipadaAnadida')}</StatusChip>
                </span>
              )}
            </RecommendationCard>
          )}
        </>
      )}
    </FlowLayout>
  )
}
